import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import {
  Upload,
  FileText,
  Download,
  Trash2,
  Star,
  Clock,
  AlertCircle,
  Loader,
  Check,
  Eye,
  Plus,
  X,
  Edit3,
  Save
} from 'lucide-react'
import { buildApiUrl, getApiHeaders } from '@/config/api'

interface Resume {
  uuid: string
  original_filename: string
  file_size: number
  file_type: string
  is_primary: boolean
  is_coverletter: boolean
  resume_description?: string
  upload_status: string
  created_at: string
  updated_at: string
}

export default function ResumeManagementPage() {
  const [resumes, setResumes] = useState<Resume[]>([])
  const [activeTab, setActiveTab] = useState<'resumes' | 'coverletters'>('resumes')
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [editingDescription, setEditingDescription] = useState<string | null>(null)
  const [descriptionValue, setDescriptionValue] = useState('')
  const [updatingDescription, setUpdatingDescription] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Filter resumes and cover letters
  const resumeList = resumes.filter(doc => !doc.is_coverletter)
  const coverLetterList = resumes.filter(doc => doc.is_coverletter)
  const currentList = activeTab === 'resumes' ? resumeList : coverLetterList

  useEffect(() => {
    loadResumes()
  }, [])

  const loadResumes = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await fetch(buildApiUrl('file-uploads/resumes'), {
        method: 'GET',
        headers: getApiHeaders(),
        mode: 'cors',
        credentials: 'omit'
      })

      if (!response.ok) {
        throw new Error(`Failed to load resumes: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      
      if (data.success && data.resumes) {
        setResumes(data.resumes)
      } else {
        throw new Error(data.error || 'Failed to load resumes')
      }
    } catch (err) {
      console.error('Error loading resumes:', err)
      setError(err instanceof Error ? err.message : 'Failed to load resumes')
    } finally {
      setLoading(false)
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const isCoverLetter = activeTab === 'coverletters'
      uploadDocument(file, isCoverLetter)
    }
  }

  const uploadDocument = async (file: File, isCoverLetter: boolean = false) => {
    try {
      setUploading(true)
      setUploadProgress(0)
      setError(null)

      const documentType = isCoverLetter ? 'cover letter' : 'resume'
      console.log(`🔄 Starting ${documentType} upload for file:`, file.name, file.size, 'bytes')

      // Step 1: Get presigned URL
      console.log('📡 Step 1: Getting presigned URL...')
      
      const presignedResponse = await fetch(buildApiUrl('file-uploads/resume/presigned-url'), {
        method: 'POST',
        headers: getApiHeaders(),
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify({
          original_filename: file.name,
          file_size: file.size,
          mime_type: file.type,
          is_coverletter: isCoverLetter
        })
      })
      
      if (!presignedResponse.ok) {
        throw new Error(`Presigned URL request failed: ${presignedResponse.status} ${presignedResponse.statusText}`)
      }
      
      const presignedData = await presignedResponse.json()

      if (!presignedData || !presignedData.success) {
        throw new Error(presignedData?.error || 'Failed to get upload URL')
      }

      const { upload_url, fields, s3_key, resume_uuid } = presignedData
      
      if (!upload_url || !fields || !s3_key) {
        throw new Error('Invalid presigned URL response')
      }

      console.log('✅ Got presigned URL:', upload_url)
      setUploadProgress(25)

      // Step 2: Upload to S3
      console.log('☁️ Step 2: Uploading to S3...')
      const formData = new FormData()
      
      // Add all fields from presigned URL
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value as string)
      })
      
      // Add the file
      formData.append('file', file)

      const uploadResponse = await fetch(upload_url, {
        method: 'POST',
        body: formData
      })

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text()
        console.error('❌ S3 upload failed:', errorText)
        throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`)
      }

      console.log('✅ S3 upload successful')
      setUploadProgress(75)

      // Step 3: Confirm upload and create document record
      console.log(`✅ Step 3: Confirming upload and creating ${documentType} record...`)
      
      const confirmPayload = {
        s3_key: s3_key,
        original_filename: file.name,
        file_size: file.size,
        mime_type: file.type,
        is_coverletter: isCoverLetter,
        is_loaded_by_recruiter: false
      }
      
      const confirmResponse = await fetch(buildApiUrl('file-uploads/resume/confirm-upload'), {
        method: 'POST',
        headers: getApiHeaders(),
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify(confirmPayload)
      })
      
      if (!confirmResponse.ok) {
        throw new Error(`Confirmation request failed: ${confirmResponse.status} ${confirmResponse.statusText}`)
      }
      
      const confirmData = await confirmResponse.json()

      if (!confirmData || !confirmData.success) {
        throw new Error(confirmData?.error || 'Failed to confirm upload')
      }

      setUploadProgress(100)
      console.log('🎉 Resume upload complete!')
      
      // Reload resumes list
      await loadResumes()

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

    } catch (error) {
      console.error('❌ Resume upload error:', error)
      setError(error instanceof Error ? error.message : 'Upload failed')
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const handleSetPrimary = async (resumeUuid: string) => {
    try {
      const response = await fetch(buildApiUrl(`file-uploads/resume/set-primary?resume_uuid=${encodeURIComponent(resumeUuid)}`), {
        method: 'POST',
        headers: getApiHeaders(),
        mode: 'cors',
        credentials: 'omit'
      })

      if (!response.ok) {
        throw new Error(`Set primary request failed: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      
      if (data.success) {
        // Update local state to reflect the change
        setResumes(prev => prev.map(resume => ({
          ...resume,
          is_primary: resume.uuid === resumeUuid
        })))
        
        alert('Resume set as default successfully!')
      } else {
        throw new Error(data.error || 'Failed to set resume as default')
      }
    } catch (error) {
      console.error('Error setting primary resume:', error)
      alert('Failed to set resume as default')
    }
  }

  const handleDownload = async (resumeUuid: string) => {
    try {
      const response = await fetch(buildApiUrl(`file-uploads/resume/${resumeUuid}/download`), {
        method: 'GET',
        headers: getApiHeaders(),
        mode: 'cors',
        credentials: 'omit'
      })

      if (!response.ok) {
        throw new Error(`Download request failed: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      
      if (data.success && data.download_url) {
        // Directly open the presigned download URL (valid for 10 seconds)
        window.open(data.download_url, '_blank')
      } else {
        throw new Error(data.error || 'Failed to get download URL')
      }
    } catch (error) {
      console.error('Error downloading resume:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      if (errorMessage.includes('500') || errorMessage.includes('Internal Server Error')) {
        alert('Resume download temporarily unavailable. Please try again later or re-upload your resume.')
      } else {
        alert('Failed to download resume: ' + errorMessage)
      }
    }
  }

  const handleDelete = async (resumeUuid: string) => {
    if (!window.confirm('Are you sure you want to delete this resume? This action cannot be undone.')) {
      return
    }

    try {
      const response = await fetch(buildApiUrl('file-uploads/resume'), {
        method: 'DELETE',
        headers: getApiHeaders(),
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify({
          resume_uuid: resumeUuid
        })
      })

      if (!response.ok) {
        throw new Error(`Delete request failed: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      
      if (data.success) {
        // Remove from local state
        setResumes(prev => prev.filter(resume => resume.uuid !== resumeUuid))
        alert('Resume deleted successfully!')
      } else {
        throw new Error(data.error || 'Failed to delete resume')
      }
    } catch (error) {
      console.error('Error deleting resume:', error)
      alert('Failed to delete resume')
    }
  }

  const handleEditDescription = (resumeUuid: string, currentDescription?: string) => {
    setEditingDescription(resumeUuid)
    setDescriptionValue(currentDescription || '')
  }

  const handleCancelEdit = () => {
    setEditingDescription(null)
    setDescriptionValue('')
  }

  const handleSaveDescription = async (resumeUuid: string) => {
    try {
      setUpdatingDescription(true)
      
      const response = await fetch(buildApiUrl('file-uploads/resume/description'), {
        method: 'PATCH',
        headers: getApiHeaders(),
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify({
          resume_uuid: resumeUuid,
          description: descriptionValue
        })
      })

      if (!response.ok) {
        throw new Error(`Update description failed: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      
      if (data.success) {
        // Update local state
        setResumes(prev => prev.map(resume => 
          resume.uuid === resumeUuid 
            ? { ...resume, resume_description: descriptionValue }
            : resume
        ))
        setEditingDescription(null)
        setDescriptionValue('')
      } else {
        throw new Error(data.error || 'Failed to update description')
      }
    } catch (error) {
      console.error('Error updating description:', error)
      alert('Failed to update description')
    } finally {
      setUpdatingDescription(false)
    }
  }

  const triggerFileSelect = () => {
    fileInputRef.current?.click()
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
      case 'uploaded':
        return 'bg-green-100 text-green-800'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'failed':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading resumes...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Document Management</h1>
          <p className="text-gray-600">Upload, manage, and organize your resumes and cover letters. Set primary documents for applications.</p>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('resumes')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'resumes'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Resumes ({resumeList.length})
              </button>
              <button
                onClick={() => setActiveTab('coverletters')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'coverletters'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Cover Letters ({coverLetterList.length})
              </button>
            </nav>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-800">{error}</p>
              <button 
                onClick={() => setError(null)} 
                className="ml-auto text-red-600 hover:text-red-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Upload Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Upload New {activeTab === 'resumes' ? 'Resume' : 'Cover Letter'}
          </h2>
          
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            {uploading ? (
              <div className="space-y-4">
                <Loader className="w-12 h-12 animate-spin mx-auto text-blue-600" />
                <div>
                  <p className="text-gray-600 mb-2">
                    Uploading {activeTab === 'resumes' ? 'resume' : 'cover letter'}...
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-2 max-w-xs mx-auto">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{uploadProgress}%</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <Upload className="w-12 h-12 mx-auto text-gray-400" />
                <div>
                  <p className="text-lg font-medium text-gray-900">
                    Upload {activeTab === 'resumes' ? 'Resume' : 'Cover Letter'}
                  </p>
                  <p className="text-gray-600">Support for PDF, DOC, and DOCX files up to 5MB</p>
                </div>
                <button
                  onClick={triggerFileSelect}
                  disabled={uploading}
                  className="btn-primary"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Choose File
                </button>
              </div>
            )}
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />
        </div>

        {/* Resumes List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Your {activeTab === 'resumes' ? 'Resumes' : 'Cover Letters'}
            </h2>
            <p className="text-gray-600 mt-1">
              Manage your uploaded {activeTab === 'resumes' ? 'resumes' : 'cover letters'} and set a primary one for applications
            </p>
          </div>

          {currentList.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No {activeTab === 'resumes' ? 'resumes' : 'cover letters'} uploaded
              </h3>
              <p className="text-gray-600 mb-4">
                Upload your first {activeTab === 'resumes' ? 'resume' : 'cover letter'} to get started
              </p>
              <button
                onClick={triggerFileSelect}
                className="btn-primary"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload {activeTab === 'resumes' ? 'Resume' : 'Cover Letter'}
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {currentList.map((resume, index) => (
                <motion.div
                  key={resume.uuid}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-6 h-6 text-blue-600" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-medium text-gray-900">{resume.original_filename}</h3>
                          {resume.is_primary && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              <Star className="w-3 h-3 mr-1" />
                              Default
                            </span>
                          )}
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(resume.upload_status)}`}>
                            {resume.upload_status}
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                          <span>{formatFileSize(resume.file_size)}</span>
                          <span>•</span>
                          <span>{resume.file_type.toUpperCase()}</span>
                          <span>•</span>
                          <div className="flex items-center space-x-1">
                            <Clock className="w-3 h-3" />
                            <span>Uploaded {formatDate(resume.created_at)}</span>
                          </div>
                        </div>
                        
                        {/* Resume Description */}
                        <div className="mt-2">
                          {editingDescription === resume.uuid ? (
                            <div className="space-y-2">
                              <textarea
                                value={descriptionValue}
                                onChange={(e) => setDescriptionValue(e.target.value)}
                                placeholder="Add a description for this resume (e.g., 'Updated with latest experience', 'Tailored for frontend roles', etc.)"
                                className="w-full p-2 text-sm border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                                rows={2}
                                maxLength={500}
                                disabled={updatingDescription}
                              />
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-500">
                                  {descriptionValue.length}/500 characters
                                </span>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={handleCancelEdit}
                                    disabled={updatingDescription}
                                    className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={() => handleSaveDescription(resume.uuid)}
                                    disabled={updatingDescription}
                                    className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-1"
                                  >
                                    {updatingDescription ? (
                                      <>
                                        <Loader className="w-3 h-3 animate-spin" />
                                        <span>Saving...</span>
                                      </>
                                    ) : (
                                      <>
                                        <Save className="w-3 h-3" />
                                        <span>Save</span>
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                {resume.resume_description ? (
                                  <p className="text-sm text-gray-600 italic">
                                    "{resume.resume_description}"
                                  </p>
                                ) : (
                                  <p className="text-sm text-gray-400 italic">
                                    No description added
                                  </p>
                                )}
                              </div>
                              <button
                                onClick={() => handleEditDescription(resume.uuid, resume.resume_description)}
                                className="ml-2 p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                title="Edit description"
                              >
                                <Edit3 className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {!resume.is_primary && (
                        <button
                          onClick={() => handleSetPrimary(resume.uuid)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Set as default resume"
                        >
                          <Star className="w-4 h-4" />
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleDownload(resume.uuid)}
                        className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Download resume"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => handleDelete(resume.uuid)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete resume"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Tips Section */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-sm font-medium text-blue-900 mb-2">Tips for Resume Management</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Upload multiple versions of your resume for different job types</li>
            <li>• Add descriptions to help you identify each resume version</li>
            <li>• Set your most general resume as the default for quick applications</li>
            <li>• Keep your resumes updated with your latest experience and skills</li>
            <li>• Supported formats: PDF (recommended), DOC, DOCX</li>
            <li>• Maximum file size: 5MB per resume</li>
          </ul>
        </div>
      </div>
    )
  }
